/* eslint-disable no-nested-ternary */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { ActionIcon, Box, Flex, Group, Space, Stack, Text, ThemeIcon } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { openConfirmModal } from '@mantine/modals';
import { IconCurrencyEuro, IconTrashX, IconX } from '@tabler/icons';
import { useContext } from 'react';
import Moment from 'react-moment';
import { useNavigate } from 'react-router-dom';
import { FormatS } from '../../../../Service/FormatS';
import { ToastS } from '../../../../Service/ToastS';
import { DocumentS } from '../../../../Templates/Documents/DocumentS';
import { TestS } from '../../../../TestS';
import { DocumentContext } from '../../../../Service/Context/DocumentContextV2';
import { PartialInvoiceDto } from '../../../../Service/DocumentT';
import { ThemeText } from '../../../../Atoms/ThemeText';
import LinkText from '../../../../Atoms/LinkText';
import PartialInvoiceHeader from './PartialInvoiceHeader';

const DocumentPartialInvoices = () => {
  const { bookitupDocument, refresh, negativeSum } = useContext(DocumentContext);
  const navigate = useNavigate();
  const { ref, width } = useElementSize();
  const { partialInvoices, eventId, finalSum, finalTaxSummary, currency, documentType } = bookitupDocument ?? {
    partialInvoices: [],
    currency: 'EUR',
  };
  const previewVersion = width < 700;

  if (partialInvoices.length === 0 || documentType === 'REMINDER') {
    return null;
  }

  const confirmRemovePartialInvoice = (invoiceId: number) => {
    openConfirmModal({
      size: 'sm',
      title: (
        <Text mt="md" weight="bolder" size="xl" align="center" sx={{ lineHeight: 1.2 }}>
          Wirklich löschen
        </Text>
      ),
      closeOnEscape: true,
      transition: 'slide-down',
      labels: { confirm: 'Ja', cancel: 'Abbrechen' },
      children: <Text>Möchten Sie diese Abschlagsrechnung wirklich entfernen?</Text>,
      onConfirm: () => removePartialInvoice(invoiceId),
    });
  };

  const removePartialInvoice = (invoiceId: number) => {
    DocumentS.patchDocument(invoiceId, { finalDocument: null }).then((ok) => {
      if (ok) {
        refresh();
      } else {
        ToastS.generalError();
      }
    });
  };

  const mapInvoiceUI = (invoice: PartialInvoiceDto) => {
    const { id, gross, vat, net, documentNumber, date, taxRate } = invoice;
    const creationDate = new Date(date);

    return (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>
        {previewVersion ? (
          <>
            <Flex sx={(theme) => ({ color: theme.colorScheme === 'dark' ? 'white' : 'black' })}>
              <Text sx={{ flexGrow: 1 }}>
                <ThemeIcon radius="xl" size="sm" mr="md" color="green">
                  <IconCurrencyEuro size={14} />
                </ThemeIcon>
                <LinkText span onClick={() => navigate(`/events/${eventId}/documents/${id}/preview`)}>
                  {` ${documentNumber} `}
                </LinkText>
                {'  '}vom{'  '}
                <Moment date={creationDate} format="DD.MM.YYYY" />
              </Text>
              <ActionIcon size="sm" sx={{ float: 'right' }}>
                <IconTrashX onClick={() => confirmRemovePartialInvoice(id)} />
              </ActionIcon>
            </Flex>
            <Flex
              direction="row"
              wrap="wrap"
              gap="md"
              rowGap="xs"
              sx={(theme) => ({ color: theme.colorScheme === 'dark' ? 'white' : 'black' })}
            >
              <Text size="xs" weight="bolder">
                Brutto:
                <Text span weight="normal">
                  {` ${FormatS.formatPriceWithCurrency(currency, gross)}`}
                </Text>
              </Text>
              <Text size="xs" weight="bolder">
                Steuer:
                <Text span weight="normal">
                  {` ${taxRate || '____'} %`}
                </Text>
              </Text>
              <Text size="xs" weight="bolder">
                USt:
                <Text span weight="normal">
                  {` ${FormatS.formatPriceWithCurrency(currency, vat)}`}
                </Text>
              </Text>
            </Flex>
          </>
        ) : (
          <Flex sx={(theme) => ({ color: theme.colorScheme === 'dark' ? 'white' : 'black' })}>
            <Text sx={{ flexGrow: 1 }}>
              <ThemeIcon radius="xl" size="sm" mr="md" color="green">
                <IconCurrencyEuro size={14} />
              </ThemeIcon>
              Abschlagsrechnung
              <Text
                span
                color="blue"
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/events/${eventId}/documents/${id}/preview`)}
              >
                {` ${documentNumber} `}
              </Text>
              vom{'  '}
              <Moment date={creationDate} format="DD.MM.YYYY" />
              {`  (USt: ${taxRate}%) `}
            </Text>
            <Text sx={{ width: 100 }} data-testid={TestS.Documents.Edit.PARTIAL_INVOICE_NET}>
              {FormatS.formatPriceWithCurrency(currency, net)}
            </Text>
            <Text sx={{ width: 100 }} data-testid={TestS.Documents.Edit.PARTIAL_INVOICE_TAX}>
              {FormatS.formatPriceWithCurrency(currency, vat)}
            </Text>
            <Text sx={{ width: 100 }} data-testid={TestS.Documents.Edit.PARTIAL_INVOICE_GROSS}>
              - {FormatS.formatPriceWithCurrency(currency, gross)}
            </Text>
            <div style={{ width: 95 }}>
              <ActionIcon size="sm" sx={{ float: 'right' }}>
                <IconX onClick={() => confirmRemovePartialInvoice(id)} />
              </ActionIcon>
            </div>
          </Flex>
        )}
      </>
    );
  };

  return (
    <div ref={ref}>
      <Space h="md" />
      <PartialInvoiceHeader desktopVersion={!previewVersion} />
      <Space h={5} />
      <Stack
        sx={(theme) => ({
          border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[1]}`,
          padding: 15,
          borderRadius: 6,
        })}
      >
        {partialInvoices.map(mapInvoiceUI)}
      </Stack>
      <Group position="right">
        <Box
          p="xs"
          my="xl"
          sx={(theme) => ({
            backgroundColor: negativeSum
              ? '#FFEFEF'
              : theme.colorScheme === 'dark'
              ? theme.colors.dark[5]
              : theme.colors.gray[0],
            borderRadius: 10,
            width: '100%',
            maxWidth: 375,
            border: negativeSum
              ? 'none'
              : `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[1]}`,
            color: negativeSum ? 'red' : 'inherit',
          })}
        >
          <Group position="apart" sx={(theme) => ({ color: theme.colorScheme === 'dark' ? 'white' : 'black' })}>
            <Text weight="bolder">Forderungsbetrag</Text>
            <Text weight="bolder" color={negativeSum ? 'red' : 'inherit'} data-testid={TestS.Documents.Edit.FINAL_SUM}>
              {finalSum && FormatS.formatPriceWithCurrency(currency, finalSum)}
            </Text>
          </Group>
          {finalTaxSummary &&
            finalTaxSummary.taxEntries.map((taxEntry: any) =>
              taxEntry.taxRatePercent === '0' ? null : (
                <ThemeText size="xs" key={taxEntry} data-testid={TestS.Documents.Edit.FINAL_TAX}>
                  {`enthält USt. ${taxEntry.taxRatePercent} % (${FormatS.formatPriceWithCurrency(
                    currency,
                    taxEntry.taxAmount,
                  )})`}
                </ThemeText>
              ),
            )}
        </Box>
      </Group>
    </div>
  );
};

export default DocumentPartialInvoices;
